import React, { useState } from 'react';
import MoreMenu, { ColorPicker, COLORS, colorName, swatch } from './MoreMenu';
import useReorder from './useReorder';
import { PeopleIcon } from './TodoPeople';
import { Icon } from './icons';
import { FoldHead, useFolds } from './TodoSmartNav';
import { FilterMark } from './TodoFilters';
import { privateMenuItems } from './TodoPrivate';

/**
 * A name typed in place: a new list, a new label, a new section, or one of
 * them renamed. Enter keeps it, Escape (or Cancel) leaves it as it was.
 * `withColor` adds the colour choice a new list or label is made with.
 */
export function NameForm({ label, initial = '', placeholder, withColor = false, submit = 'Add',
                           onSave, onCancel, className = '' }) {
  const [name, setName] = useState(initial);
  const [color, setColor] = useState('');
  const save = (e) => {
    e.preventDefault();
    const n = name.trim();
    if (!n) return;
    onSave(n, color || null);
  };
  return (
    <form className={`tda-name-form ${className}`} onSubmit={save}
          onKeyDown={(e) => { if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); onCancel(); } }}>
      <input type="text" value={name} autoFocus maxLength={120} aria-label={label}
             placeholder={placeholder} onChange={(e) => setName(e.target.value)} />
      {withColor && (
        <select value={color} aria-label={`${label}: colour`} onChange={(e) => setColor(e.target.value)}>
          <option value="">No colour</option>
          {COLORS.map((c) => <option key={c} value={c}>{colorName(c)}</option>)}
        </select>
      )}
      <span className="tda-name-buttons">
        <button type="submit" className="btn btn-primary btn-small" disabled={!name.trim()}>{submit}</button>
        <button type="button" className="btn btn-secondary btn-small" onClick={onCancel}>Cancel</button>
      </span>
    </form>
  );
}

/** The ⋯ of a list - in the sidebar and in the list's own header. The Private list's (089) is its own, shorter one. */
export const listMenuItems = (list, { actions, index = null, count = 0, onRename }) => (list.is_private
  ? privateMenuItems(list, { actions, onRename }) : [
  { label: 'Rename', icon: 'edit', onClick: onRename },
  { label: 'Share…', icon: 'share', onClick: () => actions.shareList(list) },
  actions.clientList && list.role !== 'editor' && { label: list.company ? `Client: ${list.company.name}…` : 'Client…',
                                                     icon: 'briefcase', onClick: () => actions.clientList(list) },
  actions.saveAsTemplate && { label: 'Save as template…', icon: 'template', onClick: () => actions.saveAsTemplate(list) },
  { label: 'Colour', icon: 'tag', sub: (close) => (
    <ColorPicker name={list.name} value={list.color} onPick={(c) => { close(); actions.colorList(list, c); }} />) },
  index !== null && { sep: true },
  index !== null && index > 0 && { label: 'Move up', icon: 'arrowUp', onClick: () => actions.moveList(index, index - 1) },
  index !== null && index < count - 1 && { label: 'Move down', icon: 'arrowDown', onClick: () => actions.moveList(index, index + 1) },
  { sep: true },
  { label: 'Archive', icon: 'empty', onClick: () => actions.archiveList(list) },
  { label: 'Delete…', icon: 'trash', danger: true, onClick: () => actions.deleteList(list) }
]);

/**
 * THE COLUMN DOWN THE LEFT, calmer (Oct 2026, todo-ui-proposal.md 3.1):
 *
 *   [ + Add to-do  Q ] [ ⌕  Ctrl K ]
 *   Inbox · Today · Upcoming · Assigned · Notes · More ▸     six, always
 *   FAVOURITES     only what the person pinned with a ☆ (drag to order)
 *   MY LISTS  + ▾  their lists (Inbox has gone up top); ▾ holds
 *                  "New list from template…" and "Archived lists"
 *
 * MORE opens in place (and stays open or shut, on this device) with every
 * view that used to have a row of its own - All, Flagged, Priority 1,
 * Waiting, Overdue, Scheduled, No date, Completed - and the pages: Filters
 * & labels, Templates and Team workload. Each has a ☆ that pins it.
 *
 * Every entry keeps the attribute it always had (data-view, data-list,
 * data-filter, data-label) wherever it is drawn now. TodoApp works out what
 * the entries are (counts, what the server can do, staff or contractor) and
 * this only draws them. On a phone the same column is the bottom sheet
 * under the "Today ▾" switcher; choosing anything closes it.
 */
export default function TodoSidebar({
  who, fixed, more, favourites, lists, listCount, onSelect, busy, actions, archived = null,
  canPin = false, onPin, onMoveFav, moreOpen, onMore, onAddTodo, onPalette, onShortcuts,
  onFromTemplate = null, folds: canFold = false, tip = null, paletteKeys = 'Ctrl K', isOn,
  showArchived = false, setShowArchived
}) {
  const folds = useFolds(canFold ? who : null);
  const shut = (g) => canFold && folds.isFolded(g);
  const [adding, setAdding] = useState(false);
  const [renaming, setRenaming] = useState(null);   // list id
  const reorder = useReorder((group, from, to) => {
    if (group === 'lists') actions.moveList(from, to);
    if (group === 'favs' && onMoveFav) onMoveFav(from, to);
  });
  // The Inbox and (089) Private are drawn among the fixed entries above, not here.
  const others = lists.filter((l) => !l.is_inbox && !l.is_private);
  // Pinned views and labels come first and drag; pinned filters follow, in the Filters page's order.
  const favCount = favourites.filter((e) => e.favKey).length;

  const navButton = (e, extra = {}) => (
    <button type="button" {...e.attrs}
            className={`tda-nav-item${isOn(e) ? ' is-on' : ''}${e.late ? ' is-late' : ''}${extra.className || ''}`}
            aria-current={isOn(e) ? 'page' : undefined} title={e.title || undefined}
            onClick={() => onSelect(e.sel)}>
      {e.mark || <span className={`tda-dot is-icon${e.tag ? ` is-tag ${swatch(e.color)}` : ''}`} aria-hidden="true">
        <Icon name={e.icon} /></span>}
      <span className="tda-nav-name">{e.label}</span>
      <span className="tda-n">{e.count || ''}</span>
    </button>
  );
  const star = (e, on) => (canPin && (e.favKey || e.unpin) ? (
    <button type="button" className={`tda-star${on ? ' is-on' : ''}`} aria-pressed={on}
            aria-label={on ? `Unpin ${e.label} from Favourites` : `Pin ${e.label} to Favourites`}
            title={on ? 'In Favourites - unpin' : 'Pin to Favourites'}
            onClick={() => (e.unpin && on ? e.unpin() : onPin(e.favKey, !on, e))}>
      <Icon name={on ? 'starFilled' : 'star'} size={14} />
    </button>
  ) : null);

  return (
    <nav className="tda-side is-calm" aria-label="To-do lists">
      <div className="tda-side-top">
        <button type="button" className="tda-add-todo" onClick={onAddTodo}
                title="Add a to-do (Q)">
          <Icon name="plus" size={15} /><span>Add to-do</span><kbd aria-hidden="true">Q</kbd>
        </button>
        <button type="button" className="tda-find" onClick={onPalette}
                aria-label={`Search and go anywhere (${paletteKeys})`} title={`Search and go anywhere (${paletteKeys})`}>
          <Icon name="search" size={15} /><kbd aria-hidden="true">{paletteKeys}</kbd>
        </button>
      </div>

      <ul className="tda-nav tda-views tda-fixed" aria-label="Views">
        {fixed.map((e) => (
          <li key={e.id} className="tda-nav-row">
            {e.id === 'more' ? (
              <button type="button" data-view="more" className={`tda-nav-item tda-more-toggle${moreOpen ? ' is-open' : ''}`}
                      aria-expanded={moreOpen} aria-controls="tda-more-list" onClick={() => onMore(!moreOpen)}>
                <span className="tda-dot is-icon" aria-hidden="true"><Icon name="more" /></span>
                <span className="tda-nav-name">More</span>
                <span className="tda-n tda-caret" aria-hidden="true">
                  <Icon name={moreOpen ? 'chevronDown' : 'chevronRight'} size={13} />
                </span>
              </button>
            ) : navButton(e)}
          </li>
        ))}
      </ul>
      {moreOpen && (
        <ul className="tda-nav tda-more-list" id="tda-more-list" aria-label="More">
          {more.map((e) => (
            <li key={e.id} className={`tda-nav-row${canPin && e.favKey ? ' has-star' : ''}`}>
              {navButton(e)}
              {star(e, Boolean(e.pinned))}
            </li>
          ))}
          {canPin && favourites.length === 0 && (
            <li className="tda-more-hint" aria-hidden="true">
              <Icon name="star" size={12} /> pins what you use to Favourites
            </li>
          )}
          {onShortcuts && (
            <li className="tda-nav-row">
              <button type="button" className="tda-nav-item tda-keys-link" onClick={onShortcuts}>
                <span className="tda-dot is-icon" aria-hidden="true"><Icon name="keyboard" /></span>
                <span className="tda-nav-name">Keyboard shortcuts</span>
                <kbd className="tda-n" aria-hidden="true">?</kbd>
              </button>
            </li>
          )}
        </ul>
      )}

      {favourites.length > 0 && (
        <>
          {canFold ? <FoldHead group="favourites" title="Favourites" folds={folds} controls="tda-favs-list" />
            : <div className="tda-side-head"><h3>Favourites</h3></div>}
          {!shut('favourites') && (
            <ul className="tda-nav tda-favs" aria-label="Favourites" id="tda-favs-list" data-reorder="favs">
              {favourites.map((e, i) => (
                <li key={e.id} className={`tda-nav-row has-star${e.favKey ? reorder.markOf('favs', i, favCount) : ''}`}
                    {...(e.favKey ? { 'data-reorder-item': '' } : {})} data-fav={e.favKey || e.id}>
                  {e.favKey && onMoveFav ? (
                    <span className="tda-grip" aria-hidden="true" title="Drag to reorder"
                          onPointerDown={(ev) => reorder.start(ev, 'favs', i)}><Icon name="grip" size={14} /></span>
                  ) : <span className="tda-grip-gap" aria-hidden="true" />}
                  {navButton(e)}
                  {star(e, true)}
                </li>
              ))}
            </ul>
          )}
        </>
      )}

      {canFold ? (
        <FoldHead group="lists" title="My lists" folds={folds} controls="tda-lists-list">
          <ListsHeadTools busy={busy} onAdd={() => setAdding(true)} onFromTemplate={onFromTemplate}
                          showArchived={showArchived}
                          onArchived={() => { const next = !showArchived; setShowArchived(next); if (next) actions.loadArchived(); }} />
        </FoldHead>
      ) : (
        <div className="tda-side-head">
          <h3>My lists</h3>
          <ListsHeadTools busy={busy} onAdd={() => setAdding(true)} onFromTemplate={onFromTemplate}
                          showArchived={showArchived}
                          onArchived={() => { const next = !showArchived; setShowArchived(next); if (next) actions.loadArchived(); }} />
        </div>
      )}
      {(!shut('lists') || adding) && (
        <>
          <ul className="tda-nav tda-lists" aria-label="My lists" data-reorder="lists" id="tda-lists-list">
            {others.map((l, i) => (
              <li key={l.id} className={`tda-nav-row${reorder.markOf('lists', i, others.length)}`}
                  data-reorder-item="" data-list-row={l.id}>
                {renaming === l.id ? (
                  <NameForm label={`New name for "${l.name}"`} initial={l.name} submit="Save" className="tda-rename-form"
                            onSave={(name) => { setRenaming(null); if (name !== l.name) actions.renameList(l, name); }}
                            onCancel={() => setRenaming(null)} />
                ) : (
                  <>
                    <span className="tda-grip" aria-hidden="true" title="Drag to reorder"
                          onPointerDown={(e) => reorder.start(e, 'lists', i)}><Icon name="grip" size={14} /></span>
                    <button type="button" data-list={l.id}
                            className={`tda-nav-item${isOn({ sel: { kind: 'list', id: l.id } }) ? ' is-on' : ''}`}
                            aria-current={isOn({ sel: { kind: 'list', id: l.id } }) ? 'page' : undefined}
                            onClick={() => onSelect({ kind: 'list', id: l.id })}>
                      <span className={`tda-dot ${swatch(l.color)}`} aria-hidden="true" />
                      <span className="tda-nav-name">{l.name}</span>
                      {l.shared && (
                        <span className="tda-shared" data-shared="" role="img"
                              aria-label={`Shared with ${Math.max(1, (l.members || []).length - 1)} ${(l.members || []).length === 2 ? 'person' : 'people'}`}
                              title="Shared"><PeopleIcon /></span>
                      )}
                      <span className="tda-n">{listCount(l.id) || ''}</span>
                    </button>
                    <MoreMenu label={`More for list "${l.name}"`} disabled={busy}
                              items={listMenuItems(l, { actions, index: i, count: others.length, onRename: () => setRenaming(l.id) })} />
                  </>
                )}
              </li>
            ))}
          </ul>
          {adding ? (
            <NameForm label="Name of the new list" placeholder="List name" withColor
                      onSave={(name, color) => { setAdding(false); actions.addList(name, color); }}
                      onCancel={() => setAdding(false)} />
          ) : others.length === 0 && (
            <button type="button" className="tda-add-line tda-first-list" disabled={busy} onClick={() => setAdding(true)}>
              + Make your first list
            </button>
          )}
          {/* Archived lists are out of every view, not gone: fetched only
              when asked for (My lists ▾), each with a way back. */}
          {showArchived && (
            <div className="tda-archived-box">
              <div className="tda-archived-head">
                <span>Archived lists</span>
                <button type="button" className="tda-add-line tda-archived-toggle" aria-expanded="true"
                        onClick={() => setShowArchived(false)}>Hide archived lists</button>
              </div>
              <ul className="tda-nav tda-archived" aria-label="Archived lists">
                {archived === null ? <li className="tda-quiet">Loading…</li>
                  : archived.length === 0 ? <li className="tda-quiet">Nothing archived.</li>
                    : archived.map((l) => (
                      <li key={l.id} className="tda-nav-row" data-archived-list={l.id}>
                        <span className="tda-nav-item is-archived">
                          <span className={`tda-dot ${swatch(l.color)}`} aria-hidden="true" />
                          <span className="tda-nav-name">{l.name}</span>
                        </span>
                        <button type="button" className="tda-add-line" disabled={busy}
                                aria-label={`Unarchive "${l.name}"`}
                                onClick={() => actions.unarchiveList(l)}>Unarchive</button>
                      </li>
                    ))}
              </ul>
            </div>
          )}
        </>
      )}
      {tip}
    </nav>
  );
}

/** My lists' "+" (a new list) and "▾" (from a template, the archived ones). */
function ListsHeadTools({ busy, onAdd, onFromTemplate, onArchived, showArchived }) {
  return (
    <span className="tda-head-tools">
      <button type="button" className="tda-add-btn" aria-label="Add list" title="New list"
              disabled={busy} onClick={onAdd}><Icon name="plus" /></button>
      <MoreMenu label="More for My lists" icon="chevronDown" items={[
        onFromTemplate && { label: 'New list from template…', icon: 'template', onClick: onFromTemplate },
        { label: showArchived ? 'Hide archived lists' : 'Archived lists', icon: 'empty', onClick: onArchived }
      ]} />
    </span>
  );
}

/** A saved filter, as the sidebar's Favourites show it. */
export const filterMark = (f) => <FilterMark filter={f} size={13} />;
